import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Camera, ImagePlus, Loader2, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { adminButton } from '@/components/admin/ui';
import { useToast } from '@/hooks/use-toast';
import { useDeleteJobPhoto, useJobPhotos, useUploadJobPhoto, type JobPhoto, type PhotoKind } from '@/hooks/useJobPhotos';
import { cn } from '@/lib/utils';

interface JobPhotosProps {
  jobId: string;
  orgId: string | null;
  userId: string | null;
  canUpload: boolean;
  isManager: boolean;
}

const captionSchema = z.object({ caption: z.string().trim().max(200, 'Keep captions under 200 characters') });
type CaptionValues = z.infer<typeof captionSchema>;

const KIND_LABEL: Record<PhotoKind, string> = { before: 'Before', after: 'After' };

interface Pending {
  file: File;
  kind: PhotoKind;
  preview: string;
}

function PendingPhoto({ pending, uploading, onUpload, onCancel }: {
  pending: Pending;
  uploading: boolean;
  onUpload: (caption: string) => void;
  onCancel: () => void;
}) {
  const form = useForm<CaptionValues>({ resolver: zodResolver(captionSchema), defaultValues: { caption: '' } });
  return (
    <form
      data-testid="job-photo-pending"
      onSubmit={form.handleSubmit((values) => onUpload(values.caption))}
      className="flex flex-col gap-3 rounded-xl border border-border bg-muted/40 p-3 sm:flex-row sm:items-center"
    >
      <img src={pending.preview} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-[12.5px] font-semibold">{KIND_LABEL[pending.kind]} photo</p>
        <Input aria-label="Caption (optional)" placeholder="Caption (optional)" maxLength={200} className="h-10" {...form.register('caption')} />
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" className={cn(adminButton.primary, 'h-10 flex-1 sm:flex-none')} disabled={uploading}>
          {uploading ? <Loader2 className="animate-spin" /> : null}
          Upload
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-10 w-10 p-0" aria-label="Cancel photo" onClick={onCancel} disabled={uploading}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </form>
  );
}

export function JobPhotos({ jobId, orgId, userId, canUpload, isManager }: JobPhotosProps) {
  const { toast } = useToast();
  const { data: photos = [], isLoading } = useJobPhotos(jobId);
  const upload = useUploadJobPhoto(orgId, jobId);
  const remove = useDeleteJobPhoto(jobId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickKind, setPickKind] = useState<PhotoKind>('before');
  const [pending, setPending] = useState<Pending | null>(null);

  const choose = (kind: PhotoKind) => {
    setPickKind(kind);
    inputRef.current?.click();
  };

  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'That isn’t a photo', description: 'Choose a JPG, PNG or HEIC image.', variant: 'destructive' });
      return;
    }
    if (pending) URL.revokeObjectURL(pending.preview);
    setPending({ file, kind: pickKind, preview: URL.createObjectURL(file) });
  };

  const clearPending = () => {
    if (pending) URL.revokeObjectURL(pending.preview);
    setPending(null);
  };

  const onUpload = (caption: string) => {
    if (!pending) return;
    upload.mutate(
      { file: pending.file, kind: pending.kind, caption },
      {
        onSuccess: () => {
          clearPending();
          toast({ title: 'Photo added' });
        },
        onError: (error) =>
          toast({
            title: 'Couldn’t upload that photo',
            description: (error as Error)?.message ?? 'Please try again.',
            variant: 'destructive',
          }),
      },
    );
  };

  const onDelete = (photo: JobPhoto) => {
    remove.mutate(photo, {
      onError: (error) =>
        toast({ title: 'Couldn’t remove that photo', description: (error as Error)?.message, variant: 'destructive' }),
    });
  };

  const byKind = (kind: PhotoKind) => photos.filter((p) => p.kind === kind);

  return (
    <section data-testid="job-photos" aria-label="Photos" className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-[14px] font-semibold">
          <Camera className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Photos
        </h3>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="job-photo-input" onChange={onFile} />

      {pending && (
        <PendingPhoto pending={pending} uploading={upload.isPending} onUpload={onUpload} onCancel={clearPending} />
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {(['before', 'after'] as const).map((kind) => {
          const list = byKind(kind);
          return (
            <div key={kind} className="space-y-2" data-testid={`job-photos-${kind}`}>
              <p className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{KIND_LABEL[kind]}</p>
              {list.length > 0 ? (
                <ul className="grid grid-cols-3 gap-2">
                  {list.map((photo) => (
                    <li key={photo.id} className="group relative">
                      <a
                        href={photo.url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="block aspect-square overflow-hidden rounded-lg border border-border bg-muted"
                      >
                        {photo.url ? (
                          <img src={photo.url} alt={photo.caption || `${KIND_LABEL[kind]} photo`} loading="lazy" className="h-full w-full object-cover" />
                        ) : (
                          <span className="flex h-full items-center justify-center text-[11px] text-muted-foreground">Unavailable</span>
                        )}
                      </a>
                      {photo.caption && <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">{photo.caption}</p>}
                      {(isManager || (userId && photo.uploaded_by === userId)) && (
                        <button
                          type="button"
                          aria-label="Remove photo"
                          onClick={() => onDelete(photo)}
                          className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-md bg-background/90 text-muted-foreground shadow hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[12.5px] text-muted-foreground">No {kind} photos yet.</p>
              )}
              {canUpload && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-full gap-2 text-[13.5px]"
                  onClick={() => choose(kind)}
                  disabled={upload.isPending}
                >
                  <ImagePlus className="h-4 w-4" />
                  Add {kind} photo
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
